import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'
import seoConfig from '../../config/seoPages.json'
import entityConfig from '../../config/entitySeo.json'

const { siteUrl: SITE_URL, siteName: SITE_NAME, pages, privatePrefixes } = seoConfig
const DEFAULT_IMAGE = `${SITE_URL}${seoConfig.defaultImage}`
const PAGE_BY_PATH = Object.fromEntries(pages.map((page) => [page.path, page]))
const entityId = (fragment) => `${SITE_URL}/#${fragment}`
const MARKET_REFS = entityConfig.markets.map((market) => ({ '@id': entityId(`market-${market.id}`) }))
const SERVICE_REFS = entityConfig.services.map((service) => ({ '@id': entityId(`service-${service.id}`) }))

const FAQ_ITEMS = [
    ['Where can I see my receipts and proof?', 'Open Transactions. That page is your proof trail for send money, bills, donations, tax, and other completed activity.'],
    ['How does diaspora pricing work?', 'Diaspora users usually enter the local amount first for services back home, then MojoPayr shows the home-currency debit, FX, and fee clearly before payment.'],
    ['How do local payments work?', 'Local users stay in their resident market and use the lighter local pricing path, designed to be simpler and more affordable for everyday use.'],
    ['Can I donate or contribute and still get a receipt?', 'Yes. Donations and contribution flows create receipt records that can be reopened later in Transactions.'],
]

// Application routes are intentionally kept in the existing, shared SEO
// manager. Public marketing pages continue to use seoPages.json; authenticated
// and dynamic pages use this ordered route map and remain noindex.
const APP_ROUTE_META = [
    { pattern: /^\/login$/, title: 'Sign In | MojoPayr', description: 'Sign in securely to your MojoPayr account.' },
    { pattern: /^\/register$/, title: 'Create Account | MojoPayr', description: 'Create your MojoPayr personal or business account.' },
    { pattern: /^\/forgot-password$/, title: 'Reset Password | MojoPayr', description: 'Reset your MojoPayr account password securely.' },
    { pattern: /^\/blog\/[^/]+$/, title: 'MojoPayr Blog | MojoPayr', description: 'Read the latest MojoPayr insights and updates.' },
    { pattern: /^\/pay\/invoice\/[^/]+$/, title: 'Pay Invoice | MojoPayr', description: 'Review and securely pay a MojoPayr invoice.' },
    { pattern: /^\/invoice\/[^/]+$/, title: 'Pay Invoice | MojoPayr', description: 'Review and securely pay a MojoPayr invoice.' },
    { pattern: /^\/receipt\/[^/]+$/, title: 'Transaction Receipt | MojoPayr', description: 'View and verify a MojoPayr transaction receipt.' },
    { pattern: /^\/contribute\/[^/]+$/, title: 'Group Contribution | MojoPayr', description: 'Make a secure MojoPayr group contribution.' },
    { pattern: /^\/payment\/amount$/, title: 'Payment Amount | MojoPayr', description: 'Enter the amount for your MojoPayr payment.' },
    { pattern: /^\/payment\/confirm$/, title: 'Confirm Payment | MojoPayr', description: 'Review and confirm your MojoPayr payment.' },
    { pattern: /^\/dashboard$/, title: 'Dashboard | MojoPayr', description: 'View your MojoPayr account dashboard.' },
    { pattern: /^\/service-pros\/requests\/[^/]+$/, title: 'Service Request | MojoPayr', description: 'Review and manage your MojoPayr Service Pro request.' },
    { pattern: /^\/service-pros\/manage$/, title: 'Service Pro Workspace | MojoPayr', description: 'Manage Service Pro requests, estimates, jobs, and payments.' },
    { pattern: /^\/service-pros$/, title: 'Service Pros | MojoPayr', description: 'Find and book trusted professionals through MojoPayr.' },
    { pattern: /^\/send$/, title: 'Send Money | MojoPayr', description: 'Send money securely with MojoPayr.' },
    { pattern: /^\/tickets$/, title: 'Support Tickets | MojoPayr', description: 'View and manage your MojoPayr support tickets.' },
    { pattern: /^\/wallet$/, title: 'Wallet | MojoPayr', description: 'View your MojoPayr wallet balance and activity.' },
    { pattern: /^\/transactions$/, title: 'Transactions | MojoPayr', description: 'View your MojoPayr transaction history and receipts.' },
    { pattern: /^\/notifications$/, title: 'Notifications | MojoPayr', description: 'View important MojoPayr account and payment notifications.' },
    { pattern: /^\/profile$/, title: 'Business Profile | MojoPayr', description: 'Manage your MojoPayr personal and business profile.' },
    { pattern: /^\/settings$/, title: 'Settings | MojoPayr', description: 'Manage your MojoPayr account settings.' },
    { pattern: /^\/donations$/, title: 'Donations | MojoPayr', description: 'Manage donations securely through MojoPayr.' },
    { pattern: /^\/contributions$/, title: 'Contributions | MojoPayr', description: 'View and manage MojoPayr group contributions.' },
    { pattern: /^\/invoices\/create$/, title: 'Create Invoice | MojoPayr', description: 'Create and send a MojoPayr business invoice.' },
    { pattern: /^\/invoices\/[^/]+$/, title: 'Invoice Details | MojoPayr', description: 'View and manage a MojoPayr business invoice.' },
    { pattern: /^\/invoices$/, title: 'Invoices | MojoPayr', description: 'View and manage your MojoPayr business invoices.' },
    { pattern: /^\/merchant\/scan-ticket$/, title: 'Scan Ticket | MojoPayr', description: 'Scan and validate MojoPayr event tickets.' },
    { pattern: /^\/merchant\/pos$/, title: 'Point of Sale | MojoPayr', description: 'Accept secure merchant payments through MojoPayr.' },
    { pattern: /^\/merchant\/receive-qr$/, title: 'Receive QR Payment | MojoPayr', description: 'Receive a secure MojoPayr QR payment.' },
    { pattern: /^\/merchant\/create-event$/, title: 'Create Event | MojoPayr', description: 'Create and manage a MojoPayr ticketed event.' },
    { pattern: /^\/merchant\/events\/[^/]+$/, title: 'Event Details | MojoPayr', description: 'View and manage your MojoPayr event.' },
    { pattern: /^\/merchant\/events$/, title: 'Events | MojoPayr', description: 'View and manage your MojoPayr events.' },
    { pattern: /^\/merchant\/sell-airtime$/, title: 'Sell Airtime | MojoPayr', description: 'Sell mobile airtime through your MojoPayr business account.' },
    { pattern: /^\/merchant\/business-tax$/, title: 'Business Tax | MojoPayr', description: 'Manage business tax payments through MojoPayr.' },
    { pattern: /^\/merchant\/payroll-workforce$/, title: 'Payroll & Workforce | MojoPayr', description: 'Manage payroll and workforce payments through MojoPayr.' },
    { pattern: /^\/government\/dashboard$/, title: 'Government Dashboard | MojoPayr', description: 'View the MojoPayr government services dashboard.' },
    { pattern: /^\/government\/notifications$/, title: 'Government Notifications | MojoPayr', description: 'View MojoPayr government service notifications.' },
]

const normalizePath = (pathname) => pathname.replace(/\/+$/, '') || '/'

export const resolveRouteMeta = (pathname) => {
    const path = normalizePath(pathname)
    const publicPage = PAGE_BY_PATH[path]
    const applicationPage = APP_ROUTE_META.find(({ pattern }) => pattern.test(path))

    return {
        path,
        publicPage,
        resolvedPage: publicPage || applicationPage || null,
    }
}

const upsertMeta = (selector, attributes) => {
    let tag = document.head.querySelector(selector)
    if (!tag) {
        tag = document.createElement('meta')
        document.head.appendChild(tag)
    }
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value))
}

const upsertLink = (rel, href) => {
    let tag = document.head.querySelector(`link[rel="${rel}"]`)
    if (!tag) {
        tag = document.createElement('link')
        tag.rel = rel
        document.head.appendChild(tag)
    }
    tag.href = href
}

const setSchema = (payload) => {
    let tag = document.getElementById('mojopayr-seo-schema')
    if (!tag) {
        tag = document.createElement('script')
        tag.id = 'mojopayr-seo-schema'
        tag.type = 'application/ld+json'
        document.head.appendChild(tag)
    }
    tag.textContent = JSON.stringify(payload)
}

const breadcrumbSchema = (page, canonical) => ({
    '@type': 'BreadcrumbList',
    '@id': `${canonical}#breadcrumb`,
    itemListElement: page.breadcrumbs.map((name, index) => {
        const pathParts = page.path.split('/').filter(Boolean)
        const ancestor = index === 0 ? '/' : `/${pathParts.slice(0, index).join('/')}`
        return {
            '@type': 'ListItem',
            position: index + 1,
            name,
            item: index === page.breadcrumbs.length - 1 ? canonical : `${SITE_URL}${ancestor}`,
        }
    }),
})

const createSchema = (page, canonical) => {
    const organizationId = `${SITE_URL}/#organization`
    const websiteId = `${SITE_URL}/#website`
    const financialServiceId = `${SITE_URL}/#financial-service`
    const brandId = `${SITE_URL}/#brand`
    const relatedServiceRefs = (entityConfig.pageServices[page.path] || [])
        .map((id) => ({ '@id': entityId(`service-${id}`) }))
    const pageNode = {
        '@type': page.schemaType, '@id': `${canonical}#webpage`, url: canonical,
        name: page.title, description: page.description, isPartOf: { '@id': websiteId },
        keywords: page.keywords.join(', '), inLanguage: ['en', 'fr'],
        publisher: { '@id': organizationId }, breadcrumb: { '@id': `${canonical}#breadcrumb` },
        primaryImageOfPage: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
        about: relatedServiceRefs.length ? relatedServiceRefs : { '@id': organizationId },
        ...(relatedServiceRefs.length ? { mainEntity: relatedServiceRefs } : {}),
        ...(page.schemaType === 'BlogPosting' ? {
            headline: page.title, datePublished: page.datePublished,
            author: { '@id': organizationId }, image: DEFAULT_IMAGE,
        } : {}),
        ...(page.schemaType === 'Service' ? { provider: { '@id': organizationId } } : {}),
        ...(page.path === '/faq' ? {
            mainEntity: FAQ_ITEMS.map(([question, answer]) => ({
                '@type': 'Question', name: question,
                acceptedAnswer: { '@type': 'Answer', text: answer },
            })),
        } : {}),
    }
    const graph = [
        {
            '@type': 'Organization', '@id': organizationId, name: SITE_NAME,
            alternateName: entityConfig.alternateNames, url: `${SITE_URL}/`,
            description: entityConfig.description, industry: entityConfig.industry,
            category: entityConfig.businessCategory, email: entityConfig.supportEmail,
            logo: { '@type': 'ImageObject', '@id': `${SITE_URL}/#logo`, url: `${SITE_URL}/logo.png`, caption: SITE_NAME },
            image: { '@id': `${SITE_URL}/#logo` }, brand: { '@id': brandId },
            areaServed: MARKET_REFS, knowsAbout: SERVICE_REFS,
            subjectOf: [{ '@id': `${SITE_URL}/about#webpage` }, { '@id': `${SITE_URL}/press#webpage` }],
            contactPoint: {
                '@type': 'ContactPoint', contactType: 'customer support',
                email: entityConfig.supportEmail, availableLanguage: entityConfig.languages,
                areaServed: MARKET_REFS,
            },
        },
        {
            '@type': 'Brand', '@id': brandId, name: SITE_NAME,
            alternateName: entityConfig.alternateNames,
            description: entityConfig.description, url: `${SITE_URL}/`,
            logo: { '@id': `${SITE_URL}/#logo` },
        },
        {
            '@type': 'WebSite', '@id': websiteId, url: `${SITE_URL}/`, name: SITE_NAME,
            alternateName: entityConfig.alternateNames, description: entityConfig.description,
            publisher: { '@id': organizationId }, about: { '@id': organizationId }, inLanguage: ['en', 'fr'],
            potentialAction: {
                '@type': 'SearchAction',
                target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/help-center?search={search_term_string}` },
                'query-input': 'required name=search_term_string',
            },
        },
        {
            '@type': 'FinancialService', '@id': financialServiceId, name: `${SITE_NAME} Digital Payment Platform`,
            alternateName: entityConfig.alternateNames, description: entityConfig.description,
            url: `${SITE_URL}/`, image: DEFAULT_IMAGE, parentOrganization: { '@id': organizationId },
            category: entityConfig.businessCategory, serviceType: entityConfig.services.map((service) => service.name),
            areaServed: MARKET_REFS,
            hasOfferCatalog: {
                '@type': 'OfferCatalog', name: 'MojoPayr financial technology services',
                itemListElement: SERVICE_REFS.map((service) => ({ '@type': 'Offer', itemOffered: service })),
            },
        },
        {
            '@type': 'MobileApplication', '@id': `${SITE_URL}/#mobile-application`, name: SITE_NAME,
            operatingSystem: ['Android', 'iOS'], applicationCategory: 'FinanceApplication',
            applicationSubCategory: entityConfig.businessCategory, description: entityConfig.description,
            url: `${SITE_URL}/`, publisher: { '@id': organizationId }, about: { '@id': financialServiceId },
            featureList: entityConfig.services.map((service) => service.name),
            countriesSupported: entityConfig.markets.map((market) => market.name),
        },
        {
            '@type': 'SoftwareApplication', '@id': `${SITE_URL}/#software-application`, name: SITE_NAME,
            operatingSystem: 'Web', applicationCategory: 'FinanceApplication',
            applicationSubCategory: entityConfig.businessCategory, description: entityConfig.description,
            url: `${SITE_URL}/`, publisher: { '@id': organizationId }, about: { '@id': financialServiceId },
            featureList: entityConfig.services.map((service) => service.name),
            countriesSupported: entityConfig.markets.map((market) => market.name),
        },
        ...entityConfig.markets.map((market) => ({
            '@type': 'Country', '@id': entityId(`market-${market.id}`),
            name: market.name, identifier: market.code,
        })),
        ...entityConfig.services.map((service) => ({
            '@type': 'Service', '@id': entityId(`service-${service.id}`),
            name: service.name, description: service.description,
            serviceType: service.name, provider: { '@id': organizationId }, areaServed: MARKET_REFS,
        })),
        pageNode,
        breadcrumbSchema(page, canonical),
    ]

    return { '@context': 'https://schema.org', '@graph': graph }
}

const ProductionSeo = () => {
    const location = useLocation()
    const { lang } = useLanguage()

    useEffect(() => {
        const { path, publicPage: page, resolvedPage } = resolveRouteMeta(location.pathname)
        const privateRoute = privatePrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
        const indexable = Boolean(page) && !privateRoute
        const canonical = `${SITE_URL}${path === '/' ? '/' : path}`
        const title = resolvedPage?.title || 'Page Not Found | MojoPayr'
        const description = resolvedPage?.description || 'The requested MojoPayr page could not be found.'
        const robots = indexable
            ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
            : 'noindex,nofollow,noarchive,nosnippet'

        document.documentElement.lang = lang === 'fr' ? 'fr' : 'en'
        document.title = title
        upsertLink('canonical', canonical)
        upsertMeta('meta[name="description"]', { name: 'description', content: description })
        ;['robots', 'googlebot', 'bingbot'].forEach((name) => {
            upsertMeta(`meta[name="${name}"]`, { name, content: robots })
        })
        const og = {
            'og:site_name': SITE_NAME,
            'og:type': page?.schemaType === 'BlogPosting' ? 'article' : 'website',
            'og:locale': lang === 'fr' ? 'fr_FR' : 'en_US',
            'og:url': canonical,
            'og:title': title,
            'og:description': description,
            'og:image': DEFAULT_IMAGE,
            'og:image:alt': `${title} - MojoPayr`,
            'og:image:width': '1536',
            'og:image:height': '1024',
        }
        Object.entries(og).forEach(([property, content]) => {
            upsertMeta(`meta[property="${property}"]`, { property, content })
        })
        const twitter = {
            'twitter:card': 'summary_large_image', 'twitter:title': title,
            'twitter:description': description, 'twitter:image': DEFAULT_IMAGE,
            'twitter:image:alt': `${title} - MojoPayr`,
        }
        Object.entries(twitter).forEach(([name, content]) => {
            upsertMeta(`meta[name="${name}"]`, { name, content })
        })

        if (indexable) setSchema(createSchema(page, canonical))
        else document.getElementById('mojopayr-seo-schema')?.remove()

        const verification = {
            'google-site-verification': process.env.REACT_APP_GOOGLE_SITE_VERIFICATION,
            'msvalidate.01': process.env.REACT_APP_BING_SITE_VERIFICATION,
        }
        Object.entries(verification).forEach(([name, content]) => {
            if (content) upsertMeta(`meta[name="${name}"]`, { name, content })
        })
    }, [lang, location.pathname])

    return null
}

export default ProductionSeo
